import React from "react";
import { Share2, Copy, MessageCircle, Mail, Gift } from "lucide-react";
import { toast } from "sonner";
import { useReferralShare } from "../../lib/referralShare";

/** "Share EVIQ with a friend" card — referral-aware: subscribers share
 *  their personal ?ref= link (free month per referral, 1/quarter, 4/yr);
 *  everyone else shares the generic app link. */
export const ShareAppCard = () => {
  const share = useReferralShare();

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "EVIQ — Battery Intelligence", text: share.text, url: share.url });
        return true;
      } catch (e) {
        if (e?.name === "AbortError") return true;
      }
    }
    return false;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${share.text} ${share.url}`);
      toast.success(share.isReferral ? "Referral link copied — you earn a free month when they subscribe" : "Link copied — paste it anywhere");
    } catch {
      toast.error("Couldn't copy — long-press the link instead: eviq.pro/app");
    }
  };

  const onShare = async () => {
    const ok = await shareNative();
    if (!ok) copyLink();
  };
  const body = encodeURIComponent(`${share.text} ${share.url}`);

  return (
    <div
      data-testid="share-app-card"
      className="rounded-2xl p-4 sm:p-5"
      style={{ background: "rgba(6,14,10,0.9)", border: "1px solid rgba(0,229,255,0.22)" }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] mb-1.5" style={{ color: "#00E5FF" }}>
            Spread the intelligence
          </div>
          <div className="text-white text-sm font-semibold leading-tight">Share EVIQ with a friend</div>
          <div className="text-zinc-500 text-xs mt-1 leading-relaxed">
            {share.isReferral
              ? "Your personal referral link is attached — when a friend subscribes, you earn a free month."
              : "Send them the app that watches their battery — or let them scan the code."}
          </div>
          {share.isReferral && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.14em]"
              style={{ border: "1px solid rgba(57,255,20,0.4)", color: "#39FF14", background: "rgba(57,255,20,0.07)" }}
              data-testid="share-app-referral-badge"
            >
              <Gift className="w-3 h-3" strokeWidth={2.4} />
              Referral link · {share.monthsEarned} free month{share.monthsEarned === 1 ? "" : "s"} earned
            </div>
          )}
          {share.isReferral && (
            <button
              type="button"
              onClick={copyLink}
              data-testid="share-app-referral-link"
              aria-label="Copy your referral link"
              className="mt-2.5 w-full flex items-center gap-2 rounded-xl px-3 py-2.5 text-left hover:bg-white/[0.04] transition-colors"
              style={{ border: "1px dashed rgba(0,229,255,0.35)", background: "rgba(0,229,255,0.04)" }}
            >
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 mb-0.5">Your referral link — tap to copy</div>
                <div className="font-mono text-[11px] break-all" style={{ color: "#00E5FF" }}>{share.url}</div>
              </div>
              <Copy className="w-3.5 h-3.5 shrink-0 text-zinc-400" strokeWidth={2} />
            </button>
          )}
          <button
            type="button"
            onClick={onShare}
            data-testid="share-app-button"
            className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold text-black hover:opacity-90 transition-opacity"
            style={{ background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}
          >
            <Share2 className="w-3.5 h-3.5" strokeWidth={2.4} /> Share EVIQ
          </button>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={copyLink}
              data-testid="share-app-copy"
              aria-label="Copy link"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.1em] text-zinc-300 hover:text-white transition-colors"
              style={{ border: "1px solid rgba(255,255,255,0.14)" }}
            >
              <Copy className="w-3 h-3" /> Copy link
            </button>
            <a
              href={`sms:?&body=${body}`}
              data-testid="share-app-sms"
              aria-label="Share by text message"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.1em] text-zinc-300 hover:text-white transition-colors"
              style={{ border: "1px solid rgba(255,255,255,0.14)" }}
            >
              <MessageCircle className="w-3 h-3" /> Text
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent("Check out EVIQ — Battery Intelligence")}&body=${body}`}
              data-testid="share-app-email"
              aria-label="Share by email"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.1em] text-zinc-300 hover:text-white transition-colors"
              style={{ border: "1px solid rgba(255,255,255,0.14)" }}
            >
              <Mail className="w-3 h-3" /> Email
            </a>
          </div>
        </div>
        <img
          src="/eviq-app-qr.png"
          alt="QR code — scan to open eviq.pro/app"
          data-testid="share-app-qr"
          className="shrink-0 w-20 h-20 rounded-xl"
          loading="lazy"
        />
      </div>
    </div>
  );
};
